/**
 * Theme Manager Utility
 * Handles theme switching and persistence for CamGPT
 * Themes are applied via the data-theme attribute on <html>
 */

const THEME_STORAGE_KEY = 'camgpt-theme';
const THEME_FAMILY_STORAGE_KEY = 'camgpt-theme-family';

// Theme family set from backend CompanySetting
let currentThemeFamily = 'standard';

const AVAILABLE_THEMES = [
  'standard',
  'dark',
  'corp-bank-standard',
  'corp-bank-dark',
  'cyber-punk-standard',
  'cyber-punk-dark'
];

/**
 * Set the theme family from backend configuration
 * @param {string} family - Theme family name ('standard', 'corp-bank', 'cyber-punk')
 */
export function setThemeFamily(family) {
  if (!family || family === '') {
    family = 'standard';
  }
  currentThemeFamily = family;
  localStorage.setItem(THEME_FAMILY_STORAGE_KEY, family);
}

/**
 * Get the current theme family
 * @returns {string} Theme family name
 */
export function getThemeFamily() {
  const savedFamily = localStorage.getItem(THEME_FAMILY_STORAGE_KEY);
  return savedFamily || currentThemeFamily;
}

/**
 * Build full theme name from family and variant
 * @param {string} family - Theme family ('standard', 'corp-bank', 'cyber-punk')
 * @param {boolean} isDark - Whether to use dark variant
 * @returns {string} Full theme name
 */
function buildThemeName(family, isDark) {
  if (family === 'standard') {
    return isDark ? 'dark' : 'standard';
  }
  return isDark ? `${family}-dark` : `${family}-standard`;
}

/**
 * Get the current theme from localStorage or default
 * @returns {string} Theme name
 */
export function getCurrentTheme() {
  const savedTheme = localStorage.getItem(THEME_STORAGE_KEY);

  // If no saved theme, check system preference for dark mode
  if (!savedTheme) {
    const family = getThemeFamily();
    const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    return buildThemeName(family, prefersDark);
  }

  // Validate saved theme
  return AVAILABLE_THEMES.includes(savedTheme) ? savedTheme : buildThemeName(getThemeFamily(), false);
}

/**
 * Apply theme to the document
 * @param {string} theme - Theme name
 */
export function applyTheme(theme) {
  if (!AVAILABLE_THEMES.includes(theme)) {
    console.warn(`Invalid theme: ${theme}. Using default.`);
    const family = getThemeFamily();
    theme = buildThemeName(family, false);
  }

  document.documentElement.setAttribute('data-theme', theme);
}

/**
 * Save theme preference to localStorage
 * @param {string} theme - Theme name
 */
export function saveTheme(theme) {
  localStorage.setItem(THEME_STORAGE_KEY, theme);
}

/**
 * Set and persist theme
 * @param {string} theme - Theme name
 */
export function setTheme(theme) {
  applyTheme(theme);
  saveTheme(theme);
}

/**
 * Initialize theme system
 * Call this on app startup, optionally with theme family from backend
 * @param {string} themeFamily - Optional theme family from backend
 */
export function initTheme(themeFamily = null) {
  if (themeFamily) {
    setThemeFamily(themeFamily);
  }

  const theme = getCurrentTheme();
  applyTheme(theme);

  // Listen for system theme changes
  if (window.matchMedia) {
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
      // Only auto-switch if user hasn't manually set a preference
      if (!localStorage.getItem(THEME_STORAGE_KEY)) {
        const family = getThemeFamily();
        const newTheme = buildThemeName(family, e.matches);
        applyTheme(newTheme);
      }
    });
  }

  return theme;
}

/**
 * Get list of available themes
 * @returns {Array<string>} Theme names
 */
export function getAvailableThemes() {
  return [...AVAILABLE_THEMES];
}

/**
 * Toggle between standard and dark variants of current theme family
 * @returns {string} New theme name
 */
export function toggleTheme() {
  const current = getCurrentTheme();
  const family = getThemeFamily();

  // Determine if current theme is dark variant
  const isDark = current.endsWith('-dark') || current === 'dark';

  // Toggle to opposite variant
  const newTheme = buildThemeName(family, !isDark);
  setTheme(newTheme);
  return newTheme;
}

/**
 * Get the default themes for current family
 * @returns {Object} Object with standard and dark theme names
 */
export function getDefaultThemes() {
  const family = getThemeFamily();
  return {
    standard: buildThemeName(family, false),
    dark: buildThemeName(family, true)
  };
}
